import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';
import useNotifications from 'hooks/useNotifications';
import useUserTimezone from 'hooks/useUserTimezone';

// assets
import { IconBell } from '@tabler/icons-react';

// notification filter options
const filterOptions = [
  { value: 'all', label: 'All Notifications' },
  { value: 'unread', label: 'Unread' },
  { value: 'outbound', label: 'Outbound (Alerts Sent)' },
  { value: 'inbound', label: 'Inbound (Events)' }
];

// ==============================|| NOTIFICATION ||============================== //

export default function NotificationSection() {
  const theme = useTheme();
  const navigate = useNavigate();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const formatters = useUserTimezone();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const {
    notifications,
    unreadCount,
    loading,
    error,
    filter,
    setFilter,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useNotifications();

  const handleToggle = () => {
    setOpen((prevOpen) => {
      // Refresh notifications when opening the dropdown
      if (!prevOpen) {
        refresh();
      }
      return !prevOpen;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleMarkAllRead = (e) => {
    e.preventDefault();
    markAllAsRead();
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // Close dropdown when clicking a notification
    setOpen(false);
  };

  return (
    <>
      <Box sx={{ ml: 2 }}>
        <Badge
          badgeContent={unreadCount}
          color="error"
          invisible={unreadCount === 0}
        >
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'all .2s ease-in-out',
              color: theme.vars.palette.warning.dark,
              background: theme.vars.palette.warning.light,
              '&:hover, &[aria-controls="menu-list-grow"]': {
                color: theme.vars.palette.warning.light,
                background: theme.vars.palette.warning.dark
              },
              ...theme.applyStyles('dark', {
                color: theme.vars.palette.warning.dark,
                background: theme.vars.palette.dark.main,
                '&:hover, &[aria-controls="menu-list-grow"]': {
                  color: theme.vars.palette.grey[800],
                  background: theme.vars.palette.warning.dark
                }
              })
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
          >
            <IconBell stroke={1.5} size="20px" />
          </Avatar>
        </Badge>
      </Box>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [downMD ? 5 : 0, 20] } }]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]} sx={{ minWidth: 420, maxWidth: 520 }}>
                    <Stack sx={{ gap: 2 }}>
                      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 2, px: 2 }}>
                        <Stack direction="row" sx={{ gap: 2 }}>
                          <Typography variant="subtitle1">Notifications</Typography>
                          {unreadCount > 0 && (
                            <Chip size="small" label={unreadCount} variant="filled" sx={{ color: 'background.default', bgcolor: 'warning.dark' }} />
                          )}
                        </Stack>
                        {unreadCount > 0 && (
                          <Typography
                            component={Link}
                            to="#"
                            variant="subtitle2"
                            sx={{ color: 'primary.main' }}
                            onClick={handleMarkAllRead}
                          >
                            Mark all read
                          </Typography>
                        )}
                      </Stack>
                      <Box sx={{ height: 1, maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden', '&::-webkit-scrollbar': { width: 5 } }}>
                        <Box sx={{ px: 2, pt: 0.25 }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={filter}
                            onChange={handleFilterChange}
                            slotProps={{ select: { native: true } }}
                          >
                            {filterOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </TextField>
                        </Box>
                        <Divider sx={{ mt: 2 }} />
                        {loading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : error && notifications.length === 0 ? (
                          <Box sx={{ p: 2 }}>
                            <Alert severity="warning">
                              Notifications could not be loaded. Try refreshing in a moment.
                            </Alert>
                          </Box>
                        ) : (
                          <NotificationList notifications={notifications} onItemClick={handleNotificationClick} formatters={formatters} />
                        )}
                      </Box>
                    </Stack>
                    <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                      <Button size="small" disableElevation onClick={handleViewAll}>
                        View All
                      </Button>
                    </CardActions>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
